import { z } from "zod";

export const sparkForOptions = ["specific", "orbit", "myself"] as const;
export const sparkLocationOptions = ["port-macquarie", "coastal-nsw", "forster", "tuncurry"] as const;

export type SparkForOption = (typeof sparkForOptions)[number];
export type SparkLocationOption = (typeof sparkLocationOptions)[number];

export interface SparkSyncFormValues {
  sparkFor: SparkForOption | null;
  age: string;
  gender: string;
  likes: string;
  location: SparkLocationOption | null;
  why: string;
}

export interface SparkRevealItem {
  id: string;
  category: "Sale" | "Popular" | "Rebecca’s choice" | "Wildcard";
  title: string;
  description: string;
}

export interface SparkCardData {
  title: string;
  audienceLine: string;
  locationLine: string;
  reasonLine: string;
  conciergeNote: string;
  keywords: string[];
}

export const defaultSparkSyncValues: SparkSyncFormValues = {
  sparkFor: null,
  age: "",
  gender: "",
  likes: "",
  location: null,
  why: "",
};

const blockedLocations = new Set<SparkLocationOption>();

const audienceLabels: Record<SparkForOption, string> = {
  specific: "someone specific",
  orbit: "someone in your Orbit",
  myself: "your own next indulgence",
};

const locationLabels: Record<SparkLocationOption, string> = {
  "port-macquarie": "Port Macquarie",
  "coastal-nsw": "Coastal NSW",
  forster: "Forster",
  tuncurry: "Tuncurry",
};

const audienceSchema = z.object({
  sparkFor: z.enum(sparkForOptions, {
    errorMap: () => ({ message: "Select who this Spark is for before we proceed." }),
  }),
});

const logisticsSchema = z.object({
  age: z.string().trim().min(1, "Age is required.").max(40, "Please keep the age note concise."),
  gender: z.string().trim().min(1, "Gender is required.").max(40, "Please keep the gender note concise."),
  likes: z.string().trim().min(3, "Please share at least one genuine like.").max(240, "Keep the likes summary under 240 characters."),
});

const locationSchema = z.object({
  location: z.enum(sparkLocationOptions, {
    errorMap: () => ({ message: "Please choose a destination for this Spark." }),
  }),
});

const whySchema = z.object({
  why: z.string().trim().min(24, "Please tell Rebecca why this Spark matters in at least a sentence.").max(500, "Keep your why under 500 characters."),
});

export type SparkSyncField = keyof SparkSyncFormValues;

export const isBlockedSparkLocation = (location: SparkLocationOption | null) =>
  location ? blockedLocations.has(location) : false;

export const getSparkAudienceLabel = (value: SparkForOption | null) =>
  value ? audienceLabels[value] : "someone worth delighting";

export const getSparkLocationLabel = (value: SparkLocationOption | null) =>
  value ? locationLabels[value] : "your chosen coastline";

export const validateSparkSyncStep = (
  step: 1 | 2 | 3 | 4,
  values: SparkSyncFormValues,
): Partial<Record<SparkSyncField, string>> => {
  const errors: Partial<Record<SparkSyncField, string>> = {};

  const parseResult =
    step === 1
      ? audienceSchema.safeParse(values)
      : step === 2
        ? logisticsSchema.safeParse(values)
        : step === 3
          ? locationSchema.safeParse(values)
          : whySchema.safeParse(values);

  if (parseResult.success) {
    return errors;
  }

  for (const issue of parseResult.error.issues) {
    const field = issue.path[0];
    if (typeof field === "string") {
      errors[field as SparkSyncField] = issue.message;
    }
  }

  return errors;
};

const normaliseLikes = (likes: string) =>
  likes
    .split(/,|\band\b|\//i)
    .map((value) => value.trim())
    .filter(Boolean);

const extractKeywords = (input: string) => {
  const cleaned = input
    .toLowerCase()
    .replace(/[^a-z\s-]/g, " ")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 4);

  return Array.from(new Set(cleaned)).slice(0, 4);
};

const titleCase = (value: string) =>
  value
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const buildSparkRevealItems = (values: SparkSyncFormValues): SparkRevealItem[] => {
  const likes = normaliseLikes(values.likes);
  const leadLike = likes[0] ?? "considered gifting";
  const secondLike = likes[1] ?? "an elegant everyday indulgence";
  const whyLine = values.why.trim().replace(/\s+/g, " ");
  const reasonFragment = whyLine.length > 110 ? `${whyLine.slice(0, 107).trim()}...` : whyLine;
  const location = getSparkLocationLabel(values.location);

  return [
    {
      id: "sale-1",
      category: "Sale",
      title: `${titleCase(leadLike)} on discreet markdown`,
      description: `A polished sale-led option for ${location}, chosen to honour their taste without sacrificing restraint.`,
    },
    {
      id: "sale-2",
      category: "Sale",
      title: `Coastal edit for ${secondLike}`,
      description: `A softly priced find with enough personality to feel personal, not merely practical.`,
    },
    {
      id: "popular",
      category: "Popular",
      title: `The most-saved ${titleCase(leadLike)} direction`,
      description: `A widely adored choice for this profile: flattering, useful, and elegantly easy to gift well.`,
    },
    {
      id: "rebecca",
      category: "Rebecca’s choice",
      title: `A signature pick shaped by your why`,
      description: `Rebecca’s judgement leans toward this because: “${reasonFragment}”`,
    },
    {
      id: "wildcard",
      category: "Wildcard",
      title: `An unexpected delight with social grace`,
      description: `A surprising yet safe flourish for a ${values.age}-year-old ${values.gender.toLowerCase()} who lights up around ${leadLike}.`,
    },
  ];
};

export const buildSparkCardData = (values: SparkSyncFormValues): SparkCardData => {
  const keywords = [...normaliseLikes(values.likes), ...extractKeywords(values.why)].slice(0, 4);
  const audienceLine = `This Spark is composed for ${getSparkAudienceLabel(values.sparkFor)}.`;
  const locationLine = `Filtered for ${getSparkLocationLabel(values.location)} with Rebecca’s concierge guardrails in place.`;
  const reasonLine = `Why it matters: ${values.why.trim()}`;

  return {
    title: values.sparkFor === "myself" ? "Your personal Spark card" : "Your digital Spark card",
    audienceLine,
    locationLine,
    reasonLine,
    conciergeNote:
      "Deliver this Spark with confidence: it feels considered, emotionally literate, and impossible to mistake for guesswork.",
    keywords,
  };
};